/**
 * Thrivia Embed SDK: loader entry point (v1).
 *
 * Purpose: the single framework-free bundle an organizer pastes onto their own website. Built by
 * `scripts/build-embed.mjs` into the committed artifact `public/embed/v1.js` and served from our
 * origin, so it always executes cross-origin on somebody else's domain.
 *
 * Boot sequence (spec section 6):
 *   1. locate our own `<script>` and parse the snippet configuration
 *   2. detect the host platform (`platform.ts`)
 *   3. publish `window.__thrivia` and refuse to initialise twice
 *   4. scan for mounts (`scan.ts`), binding and stamping every clickable one
 *   5. ONE batched read for every event mount on the page (`api.ts`), plus one read per creator list
 *   6. observe: a debounced `MutationObserver` for late-mounting DOM, an `IntersectionObserver` for
 *      impressions, deduped once per mount per page load
 *
 * Scope in this revision (Plan 2, Task 7): everything above, plus all four surfaces (card, list,
 * button and the framed `page`), and the checkout overlay behind a bound click (`overlay.ts`),
 * joined to the framed routes by the one origin-validated postMessage bus (`bus.ts`).
 *
 * Key features:
 * - Self-locating: every absolute URL is derived from this script tag's own `src`, never from
 *   `window.location`, which on an organizer's page is their origin and not ours.
 * - Idempotent: a second execution (two snippets, a tag manager firing twice, the shim racing a
 *   hand-pasted tag) increments a counter and returns. The first boot's config object survives.
 * - Silent by default: no console output unless `data-thrivia-debug` is set. An organizer's
 *   console is not ours to fill.
 * - Fails to a working link: if the API is unreachable, non-ok or malformed, the mount is left
 *   exactly as the organizer wrote it, baked permalink anchor and all. No error box ever renders on
 *   somebody else's homepage.
 * - Zero runtime dependencies, zero globals beyond the single `window.__thrivia` namespace.
 *
 * Snippet attributes read here (all on the `<script>` tag):
 * - `data-key`          publishable embed key, `pk_live_...`
 * - `data-api`          absolute base URL of the embed API, defaults to production
 * - `data-thrivia-autolink`  opt in to upgrading existing thrivia.io links on the page
 * - `data-thrivia-preview`   suppress ALL beacons while still rendering (dashboard preview)
 * - `data-thrivia-debug`     console diagnostics
 */

import {
  FALLBACK_ORIGIN,
  LOADER_SRC_RE,
  findScript,
  readAttr,
  readFlag,
  resolveOrigin,
  resolveUrl,
  DT,
} from "./origin";
import {
  chunkIds,
  fetchCreatorEvents,
  fetchEventCards,
  type EmbedApiResult,
  type RequestContext,
} from "./api";
import { detectPlatform, type PlatformInfo } from "./platform";
import {
  createRegistry,
  pruneMounts,
  scanMounts,
  type MountMode,
  type MountRecord,
  type MountRenderer,
} from "./scan";
import { createTracker, type Tracker } from "./track";
import { initBus } from "./bus";
import { openCheckout } from "./overlay";
import { addCss, injectStyles } from "./styles";
import type { EmbedCaptureFactory, EmbedKit, EmbedSurfaceFactory } from "./kit";
import { onCaptureWanted, registerCapture } from "./render/captureSlot";
import { C, cardRenderer, e, eventLd, formatPrice, ldScript, linkTo, swap, themeCta } from "./render/card";
import { listRenderer } from "./render/list";
import { buttonRenderer } from "./render/button";
import { initPage, pageRenderer } from "./render/page";

/** Bundle version. Bumped only when the public `v1.js` contract changes. */
const LOADER_VERSION = "1";

/**
 * Production embed API base. The loader is served from the web origin (thrivia.io or a CDN) while
 * the API lives on api.thrivia.io, so this cannot be derived from the script's own origin the way
 * asset URLs are. `data-api` overrides it, which is how the cross-origin fixture in `e2e-embed/`
 * points a local page at a local backend.
 *
 * ENDS WITH A TRAILING SLASH, which is this repo's convention for an API base
 * (`src/commons/ApiHelper.ts:3`, and Plan 1 normalized `app/e/[id]/route.ts` to the same shape).
 * Paths are therefore appended WITHOUT a leading slash: `apiBase + "embed/events?..."`. Carrying
 * two opposite normalizations of the same base is the bug class Plan 1 already shipped once.
 */
const DEFAULT_API_BASE = "https://api.thrivia.io/api/";

/**
 * Debounce on the `MutationObserver` rescan.
 *
 * Host frameworks do not add one node, they add hundreds: Webflow's interactions, Wix's renderer
 * and any React hydration all produce long mutation bursts. Rescanning per record would run a
 * document-wide `querySelectorAll` hundreds of times and, worse, could fire a separate API request
 * per burst. One scan per quiet period keeps the promise that a page makes one batched call.
 */
const RESCAN_DEBOUNCE_MS = 100;

/**
 * Hard ceiling on how long a mutation burst may postpone a rescan.
 *
 * The debounce above is trailing-only, so on its own a host that mutates faster than 100ms forever
 * (a carousel, a chat widget, a framework on a render loop) postpones the scan forever and the
 * observer discovers nothing at all. This bounds the damage to one second.
 */
const RESCAN_MAX_WAIT_MS = 1000;

/**
 * Normalizes `data-api` to exactly one trailing slash.
 *
 * `/\/*$/` and not `/\/?$/`: the `?` form is anchored at end of string, so against `.../api//` it
 * matches only the LAST slash and replaces it with itself, closing the missing-slash case while
 * silently missing the doubled one. `*` consumes the whole trailing run.
 *
 * A blank or bare-slash value falls back to the default rather than normalizing. `readAttr` already
 * trims and nulls empty strings, so this is belt and braces, but it is the failure Plan 1 actually
 * hit: a whitespace-only value normalizes to `"/"`, every request built on it becomes a RELATIVE
 * URL resolved against the organizer's origin, and every call 404s on their domain with nothing in
 * a log to say why.
 */
function normalizeApiBase(raw: string | null): string {
  const trimmed = (raw || "").trim();
  if (trimmed === "" || trimmed === "/") return DEFAULT_API_BASE;
  return trimmed.replace(/\/*$/, "/");
}

/**
 * Snippet configuration, parsed once at boot from the loader's own script tag.
 *
 * This is the contract the rest of the SDK builds on. Tasks 5 to 7 consume it; nothing else parses
 * script attributes.
 */
export interface ThriviaEmbedConfig {
  /** `data-key`. Null when the tag was pasted without one, which is a do-nothing state. */
  key: string | null;
  /** Absolute origin the bundle was served from, derived from the script tag's own `src`. */
  origin: string;
  /** Absolute URL of the loader script itself, or null when self-location failed. */
  scriptUrl: string | null;
  /**
   * Absolute embed API base, ALWAYS with exactly one trailing slash, per the repo convention in
   * `src/commons/ApiHelper.ts`. Append paths without a leading slash. `data-api` overrides
   * {@link DEFAULT_API_BASE}.
   */
  apiBase: string;
  /** `data-thrivia-autolink`: upgrade existing thrivia.io anchors into tracked openers. Opt in. */
  autolink: boolean;
  /**
   * `data-thrivia-preview="true"`: render every surface fully but send ZERO beacons.
   *
   * Exists for the dashboard's snippet preview (Plan 3), where a creator looking at their own
   * widget must not manufacture impressions and clicks against their own analytics. It sits on top
   * of, not instead of, the `sid: null` path: preview holds even when the API hands back a usable
   * session id. Enforced in `track.ts`, and also settable per mount.
   */
  preview: boolean;
  /** `data-thrivia-debug`: emit console diagnostics. Off by default. */
  debug: boolean;
}

/**
 * Which surfaces this build actually implements.
 *
 * Each flag flips in the task that implements its surface. Consumers branch on these rather than on
 * the bundle version.
 *
 * These flags are NOT the handover signal for individual elements. Whatever binds a click stamps
 * `data-thrivia-bound` (`BOUND_ATTR` in `origin.ts`) on that element, because that is what the
 * legacy shim checks before it stands down. Task 4 already binds and stamps button mounts while
 * every flag below is still false, and that is correct: the flags describe rendered surfaces.
 */
export interface ThriviaEmbedCapabilities {
  button: boolean;
  card: boolean;
  list: boolean;
  page: boolean;
  /**
   * The sticky booking bar. True even before its chunk has arrived: the flag answers "does this
   * build implement the surface", and a consumer that had to wait for a network round trip to be
   * told yes would get `false` for a surface that is about to render.
   */
  bar: boolean;
  autolink: boolean;
}

/** The one global this bundle publishes: `window.__thrivia`. */
export interface ThriviaEmbedGlobal {
  version: string;
  /** True once the first execution finished booting. */
  booted: boolean;
  /** Executions after the first. Non-zero means the page carries duplicate loader tags. */
  duplicateLoads: number;
  config: ThriviaEmbedConfig;
  capabilities: ThriviaEmbedCapabilities;
  /** Host platform, detected once at boot and tagged onto every request and beacon. */
  platform: PlatformInfo;
  /** The live mount registry in discovery order. Read by the E2E fixture and by Plan 3's preview. */
  mounts: MountRecord[];
  /**
   * Registers a lazily loaded surface. Called by a satellite bundle, never by a host page.
   *
   * This is the ONE runtime entry point on the global, and it is a property of an object we
   * published rather than an export of the IIFE: `index.ts` is still built with no `globalName`,
   * so the artifact is still the single clean IIFE `build.test.ts` pins.
   *
   * It is also the one place a host page could interfere with us, so it is worth being explicit
   * about what that is worth to an attacker: anything already running on the organizer's page can
   * draw whatever it likes wherever it likes, and does not need our registry to do it. What the
   * registry cannot be used for is reaching anything cross-origin, because it hands out no key, no
   * sid and no token - only DOM helpers the page already has.
   */
  use(mode: MountMode, factory: EmbedSurfaceFactory): void;
  /** Registers the notify-me capture. Called by `v1-capture.js`, never by a host page. */
  useCapture(factory: EmbedCaptureFactory): void;
}

type LoaderWindow = Window & typeof globalThis & { __thrivia?: ThriviaEmbedGlobal };

/** Reads the snippet configuration off the loader's own script tag. */
function parseConfig(script: HTMLScriptElement | null): ThriviaEmbedConfig {
  const src = script ? script.src || script.getAttribute("src") : null;

  return {
    key: readAttr(script, "data-key"),
    // Derived from OUR script tag, never from window.location: on an organizer's page that is
    // their origin, and every asset URL built from it 404s.
    origin: script ? resolveOrigin(src) : FALLBACK_ORIGIN,
    scriptUrl: resolveUrl(src),
    apiBase: normalizeApiBase(readAttr(script, "data-api")),
    autolink: readFlag(script, DT + "autolink"),
    preview: readFlag(script, DT + "preview"),
    debug: readFlag(script, DT + "debug"),
  };
}

/**
 * Surface renderers, one per mode.
 *
 * `card` and `list` landed in Task 5, `button` in Task 6 and `page` in Task 7, each by adding an
 * import and a key here. A renderer is not registered through a function call on
 * purpose: `index.ts` has no runtime exports (esbuild emits a clean IIFE with no `globalName` only
 * while that holds), so registration has to be a static edit in this file.
 *
 * A mode with no renderer still gets its full lifecycle, its data and its `data-thrivia-state`
 * attribute, which is what keeps the pipeline observable before a surface exists.
 */
const RENDERERS: Partial<Record<MountMode, MountRenderer>> = {
  card: cardRenderer,
  list: listRenderer,
  button: buttonRenderer,
  page: pageRenderer,
};

/**
 * Surfaces that ship as their own artifact and are fetched on first use.
 *
 * The rule for what belongs here is the byte budget, not taste: a surface most organizers do not
 * mount has no business in the bundle every organizer downloads. `bar` is the first, and the map
 * is keyed by mode because the chunk's filename is derived from it - `v1.js` beside `v1-bar.js`,
 * from the same directory, discovered from our own script tag rather than from `window.location`
 * the way every other URL in this bundle is.
 *
 * A chunk that never arrives (offline, a Content Security Policy that forbids the extra script, a
 * 404 on a half-deployed CDN) leaves the mount exactly where a mount with no renderer has always
 * been left: its state attribute is written, its skeleton is not drawn, and the organizer's baked
 * permalink anchor is still standing. The surface is missing; the page is not broken.
 */
const LAZY_SURFACES: Partial<Record<MountMode, boolean>> = { bar: true };

/**
 * Whether an href is script rather than a destination.
 *
 * Parsed with `URL`, never pattern-matched on the raw string. The href reaching this code comes from
 * three places and they are not the same shape: `getAttribute("href")` and `card.url` are raw, while
 * the nested-anchor path is the already-parsed `.href`, so a regex would be asking two different
 * questions. It is also simply wrong on the raw form: the URL parser strips ASCII tab and newline
 * before reading the scheme, so a tab spliced into `java<TAB>script:` IS a javascript: URL to the
 * browser and is not one to `/^\s*javascript:/`. Parsing asks the question the browser will answer.
 *
 * An unparseable href counts as script, i.e. we decline to act on it. Declining costs the
 * enhancement; acting on something we could not read is how a bypass gets shipped.
 */
function isScriptHref(href: string): boolean {
  try {
    return new URL(href, document.baseURI).protocol === "javascript:";
  } catch {
    return true;
  }
}

function boot(): void {
  const w = window as LoaderWindow;
  const existing = w.__thrivia;

  // Double-init guard. Two loader tags, a tag manager that fires twice, or the shim racing a
  // hand-pasted tag must produce exactly one initialisation: re-running would mean duplicate
  // observers, duplicate impressions, and duplicate checkout overlays.
  if (existing && existing.booted) {
    // `|| 0` and a guarded `config`: `window.__thrivia` is a public name on somebody else's page and
    // we are not the only thing that can write it. A squatter, a stub left by a tag manager, or an
    // older bundle shaped differently would otherwise make this line NaN and the next one throw an
    // uncaught TypeError on the organizer's homepage.
    existing.duplicateLoads = (existing.duplicateLoads || 0) + 1;
    if (existing.config && existing.config.debug) {
      console.warn(
        "[thrivia] duplicate load #" + existing.duplicateLoads,
      );
    }
    return;
  }

  const config = parseConfig(findScript(LOADER_SRC_RE));
  const platform = detectPlatform();
  const registry = createRegistry();

  // The one origin the postMessage bus will ever post to or accept from. It is OUR origin, taken
  // from our own script tag, which is also where the checkout frame and the login popup are served
  // from. Set before anything can open a frame, and never derived from `window.location`.
  initBus(config.origin);

  // Warms our origin so Book now's checkout frame skips DNS + TLS.
  const warm = e("link", "", document.head) as HTMLLinkElement;
  warm.rel = "preconnect";
  warm.href = config.origin;

  const api: ThriviaEmbedGlobal = {
    version: LOADER_VERSION,
    booted: true,
    duplicateLoads: 0,
    config: config,
    // Each flag flips in the task that RENDERS its surface. `page` waits for Task 7.
    //
    // `autolink: true` means ONE thing and it is narrower than the name suggests (R38): an existing
    // `…/e/<id>` PERMALINK is upgraded into a tracked checkout opener. A canonical
    // `…/@handle/slug` link is claimed and then passes through completely untouched: no overlay, no
    // beacon (a mount with no event id never joins a request, so it never has a sid to beacon
    // against), and no change to its navigation. Resolving a slug to an id needs an endpoint that
    // does not exist; it is on the close-out backlog, not in this build.
    capabilities: {
      button: true,
      card: true,
      list: true,
      page: true,
      bar: true,
      autolink: true,
    },
    platform: platform,
    mounts: registry.list,
    use: function (mode: MountMode, factory: EmbedSurfaceFactory): void {
      try {
        RENDERERS[mode] = factory(KIT);
        // Everything already discovered for this mode is sitting at whatever state it reached
        // while the chunk was in flight, with nothing drawn. `first` is true because this IS the
        // first render for these records: no renderer has seen them.
        repaint(mode, true);
      } catch (error) {
        log("use fail", mode, error);
      }
    },
    useCapture: function (factory: EmbedCaptureFactory): void {
      try {
        registerCapture(factory(KIT));
        // Repaint what is already on the page: the card that asked for the chunk drew its slot
        // empty, and only a repaint puts the trigger in it.
        //
        // DEFERRED, and not out of caution. The request for this chunk is made from inside a
        // card's own paint, so a host that evaluates the script synchronously (a bundler that
        // inlined it, a service worker serving it from cache) lands here part way through that
        // paint: the repaint would build a fresh card WITH the trigger, and then the paint that
        // is still on the stack would finish and swap its own triggerless node in over the top.
        // A microtask puts the repaint after the paint that asked for it, on every host.
        Promise.resolve().then(function (): void {
          for (const record of registry.list) {
            if (record.state === "ready") render(record, false);
          }
        });
      } catch (error) {
        log("capture fail", error);
      }
    },
  };

  w.__thrivia = api;

  /**
   * Debug diagnostics, off unless the snippet carries `data-thrivia-debug`.
   *
   * PARTLY SPENT (the spec section 8 cut menu, which reserved these strings and which the notify-me
   * capture drew on). What went: the per-scan mount count and the prune count, both of which
   * describe a pipeline working normally. What stayed: the boot banner, because it answers "is the
   * loader running, with which key, api base and platform" and nothing else can; "no key", because
   * "I pasted the snippet and nothing renders" is the most common support case there is; and every
   * line that names something that went WRONG, which is the only kind of line anybody has ever
   * turned this flag on to read.
   */
  const log = (...args: unknown[]): void => {
    if (config.debug) console.log("[thrivia]", ...args);
  };

  log("booted v" + LOADER_VERSION, config, platform);

  // No key, nothing to do, and specifically nothing to CLAIM. The API answers 401 without one, so
  // there is no data to render and no overlay to open, and stamping elements we cannot serve would
  // make the legacy shim stand down from clicks that then do nothing. This is also the state every
  // live legacy snippet boots into, because the old two-line snippet has no key at all: the shim
  // injects us, we find no key, and its own click handling keeps working untouched.
  if (!config.key) {
    log("no key");
    return;
  }

  // The page surface builds its own frame URLs, so it needs the two values the snippet carries.
  // Handed over here rather than read from `window.__thrivia`, which is a public name on somebody
  // else's page and therefore not a channel we should be reading our own configuration back out of.
  initPage(config.origin, config.key, registry.list);

  // The notify-me capture ships as its own chunk (`captureSlot.ts` says why). This is the call
  // that fetches it, handed over rather than reached for: the seam knows when a card wants a
  // form, and this file knows where our sibling artifacts live.
  onCaptureWanted(function (): void {
    loadChunk("capture");
  });

  const ctx: RequestContext = {
    apiBase: config.apiBase,
    key: config.key,
    platform: platform.id,
    debug: config.debug,
  };
  const tracker: Tracker = createTracker({
    apiBase: config.apiBase,
    platform: platform.id,
    preview: config.preview,
    debug: config.debug,
  });

  /**
   * What a lazily loaded surface is given to draw with. Built once; see `kit.ts` for the why.
   */
  const KIT: EmbedKit = {
    e: e,
    swap: swap,
    linkTo: linkTo,
    formatPrice: formatPrice,
    themeCta: themeCta,
    css: function (text: string): void {
      injectStyles();
      addCss(text);
    },
    click: function (record: MountRecord, event: MouseEvent): void {
      onMountClick(record, event);
    },
    impress: function (record: MountRecord): void {
      impress(record);
    },
    repaint: function (mode: MountMode): void {
      repaint(mode, false);
    },
    idAttr: DT + "id",
    intentAttr: DT + "intent",
    apiBase: config.apiBase,
    c: C,
    ld: eventLd,
    ls: ldScript,
    sb: platform.sandboxed,
  };

  /** Surfaces whose chunk we have already asked for, so a rescan does not ask twice. */
  const requested: Record<string, boolean> = {};

  /**
   * Fetches the chunk for a lazily loaded surface, at most once per page.
   *
   * Derived from our OWN script tag, like every other URL here: on an organizer's page
   * `window.location` is their origin, and a chunk URL built from it would 404 on their server.
   * A tag with no resolvable src (inlined by a tag manager, rewritten by a bundler) means we
   * cannot know where our siblings live, and the surface simply stays unrendered.
   */
  function loadChunk(name: string): void {
    if (requested[name]) return;
    requested[name] = true;
    const url = config.scriptUrl;
    if (!url) return;
    try {
      const tag = document.createElement("script");
      tag.async = true;
      tag.src = url.replace(/v1\.js/, "v1-" + name + ".js");
      // One flaky request must not disable a surface for the life of the page. `render` calls back
      // here on every state change, so clearing the flag is all a retry needs.
      tag.onerror = function (): void {
        requested[name] = false;
      };
      document.head.appendChild(tag);
    } catch (error) {
      log("chunk fail", name, error);
    }
  }

  /** Impressions. Created lazily so a page whose data never arrives never constructs one. */
  let impressionObserver: IntersectionObserver | null = null;

  /**
   * Accounts for one impression, at most once per mount per page load.
   *
   * `impressed` is set whether or not a beacon actually leaves, so preview mode and a null sid
   * suppress the beacon without arming a second attempt later.
   */
  /**
   * Re-renders every mount of one mode.
   *
   * Two callers with the same need: a chunk arriving with records already waiting, and a satellite
   * that has released a resource only one mount at a time can hold - the single bar - and wants
   * whichever runner-up is ready to take it.
   */
  function repaint(mode: MountMode, first: boolean): void {
    for (const record of registry.list) {
      if (record.mode === mode) render(record, first);
    }
  }

  function impress(record: MountRecord): void {
    if (record.impressed) return;
    record.impressed = true;
    tracker.track("embed_impression", record.sid, record.mode, record.preview);
  }

  function watchImpression(record: MountRecord): void {
    if (record.impressed) return;
    if (typeof IntersectionObserver !== "function") {
      // No observer on this host. Counting the render is closer to the truth than counting nothing.
      impress(record);
      return;
    }
    if (!impressionObserver) {
      impressionObserver = new IntersectionObserver(
        (entries) => {
          for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            if (!entry.isIntersecting) continue;
            const record = registry.byEl.get(entry.target);
            if (record) impress(record);
            if (impressionObserver) impressionObserver.unobserve(entry.target);
          }
        },
        // A sliver counts. Requiring a large fraction would miss a card that is taller than a phone
        // viewport, which is most of them.
        { threshold: 0.01 },
      );
    }
    // A viewport-pinned bar is on screen from the moment it is drawn, and its MOUNT element is an
    // empty container that may sit anywhere in the document - often far below the fold, where
    // observing it would report the bar as unseen while the visitor is looking straight at it.
    //
    // It is the SATELLITE that reports it, through `kit.impress`, and not this function: we are
    // called the moment the state flips to ready, which is before the chunk has landed and without
    // knowing whether this mount won the one-bar-per-page race. Counting here would count a bar a
    // blocked chunk never drew, and would count it twice over for two mounts of which only one is
    // ever visible.
    if (record.mode === "bar") return;
    impressionObserver.observe(record.el);
  }

  /**
   * Hands a mount to its renderer and records the state on the element.
   *
   * `data-thrivia-state` is written for every mount in every task. It is how the cross-origin
   * fixture and Task 8's E2E can see the pipeline work before any pixels exist, and it gives Task
   * 5's stylesheet a hook that does not depend on class names it has to add itself.
   */
  function render(record: MountRecord, first: boolean): void {
    try {
      record.el.setAttribute(DT + "state", record.state);
      const renderer = RENDERERS[record.mode];
      if (!renderer) {
        if (LAZY_SURFACES[record.mode]) loadChunk(record.mode);
        return;
      }
      if (first && renderer.mount) renderer.mount(record);
      else renderer.update(record);
    } catch (error) {
      // One renderer throwing must not cost the page its other mounts, and must never surface on an
      // organizer's site. The mount keeps whatever it had, which is at worst its permalink anchor.
      log("render fail", record.mode, error);
    }
  }

  /**
   * Applies one API result to the mounts it was requested for.
   *
   * Every failure mode lands in the same place: `state` changes, the element is not emptied, the
   * baked anchor is untouched, and nothing is drawn to say so.
   */
  function apply(records: MountRecord[], result: EmbedApiResult): void {
    for (let i = 0; i < records.length; i++) {
      const record = records[i];

      if (!result.ok) {
        record.state = "error";
        log("api fail", record.eventId || record.creator);
        render(record, false);
        continue;
      }

      record.sid = result.sid;

      if (record.creator !== null) {
        // `limit` is non-null for every record with a creator: `scanMounts` sets both in the same
        // call and neither is writable afterwards.
        const items = result.list.slice(0, record.limit || undefined);
        record.items = items;
        record.state = items.length > 0 ? "ready" : "missing";
      } else {
        // Keyed by the id we asked for, against the card's OWN id. The API returns index order.
        const card = record.eventId ? result.cards.get(record.eventId) : undefined;
        record.card = card || null;
        record.state = card ? "ready" : "missing";
        // The API answered and had no such event: an unpublished id or a typo in the snippet. Worth
        // telling the creator about, and reportable precisely because the request itself succeeded.
        if (!card) tracker.track("embed_error", record.sid, record.mode, record.preview);
      }

      render(record, false);
      if (record.state === "ready") watchImpression(record);
    }
  }

  /**
   * Marks a batch of mounts in flight.
   *
   * The `render` call is not cosmetic bookkeeping: `loading` is the transition Task 5's skeleton
   * exists for, so a state assignment without it would publish a `MountRenderer.update` contract
   * that quietly skips the one state a renderer most needs, and leave `data-thrivia-state` reading
   * "pending" for the whole of a request.
   */
  function markLoading(records: MountRecord[]): void {
    for (let i = 0; i < records.length; i++) {
      records[i].state = "loading";
      render(records[i], false);
    }
  }

  /** Loads data for newly discovered mounts. */
  function load(records: MountRecord[]): void {
    const eventMounts: MountRecord[] = [];
    const listMounts: MountRecord[] = [];
    for (const record of records) {
      if (record.eventId !== null) eventMounts.push(record);
      else if (record.creator !== null) listMounts.push(record);
    }

    if (eventMounts.length > 0) {
      // ONE call for every event mount on the page. The surface is the mode of the first event
      // mount in document order, because the endpoint mints one sid per response with the surface
      // baked into it and a batch can legitimately mix modes. Deterministic per page, which also
      // keeps the URL stable for the endpoint's 60 second shared cache.
      const surface = eventMounts[0].mode;
      const byId = new Map<string, MountRecord[]>();
      for (const record of eventMounts) {
        // Non-null by construction: this array was filled from the `eventId !== null` branch above.
        const id = record.eventId || "";
        const bucket = byId.get(id);
        if (bucket) bucket.push(record);
        else byId.set(id, [record]);
      }

      // Chunked only above the backend's own ceiling, which silently truncates a longer list. A
      // normal page is one chunk and therefore one request.
      for (const chunk of chunkIds(Array.from(byId.keys()))) {
        const chunkRecords: MountRecord[] = [];
        for (const id of chunk) {
          const bucket = byId.get(id);
          if (bucket) for (const record of bucket) chunkRecords.push(record);
        }
        markLoading(chunkRecords);
        fetchEventCards(chunk, surface, ctx)
          .then((result) => apply(chunkRecords, result))
          // `apply` calls into renderers, and a renderer that throws here would become an
          // unhandledrejection event on the ORGANIZER's page rather than on ours.
          .catch((error) => log("apply fail", error));
      }
    }

    // The list surface is creator-scoped, so it can never join the batch: a different endpoint, a
    // different cache key, and one request per creator mount.
    for (const record of listMounts) {
      markLoading([record]);
      fetchCreatorEvents(record.creator || "", record.limit || 0, ctx)
        .then((result) => apply([record], result))
        .catch((error) => log("list fail", error));
    }
  }

  function scan(): void {
    let found: MountRecord[];
    try {
      // Release anything the host has removed BEFORE looking for what it has added. A
      // single-page-application host does both in one mutation burst, and a replaced container is a
      // new element, so without the prune the old record survives holding a detached subtree.
      const removed = pruneMounts(registry);
      for (const record of removed) {
        if (impressionObserver) impressionObserver.unobserve(record.el);
        const renderer = RENDERERS[record.mode];
        if (renderer && renderer.teardown) renderer.teardown(record);
      }

      found = scanMounts(registry, {
        autolink: config.autolink,
        preview: config.preview,
        onClick: onMountClick,
      });
    } catch (error) {
      // Guest code. A host that has clobbered querySelectorAll, addEventListener or Element.prototype
      // costs us discovery, not their page.
      log("scan fail", error);
      return;
    }

    if (found.length === 0) return;

    for (const record of found) {
      // An autolinked link we could not resolve to an event id has no request to make and no
      // surface to draw, so it goes straight to its terminal state rather than sitting at
      // `pending` for the life of the page on markup that is not ours.
      if (record.autolink && !record.eventId) record.state = "linked";
      render(record, true);
    }

    // Autolink anchors join the batch when their href carried an event id and are skipped by
    // `load` when it did not, which is the same `eventId !== null` test every other mount takes.
    load(found);
  }

  /**
   * Where to send a visitor when the checkout surface itself cannot be used.
   *
   * `card.url` once data has arrived, because it is the canonical
   * `https://www.thrivia.io/@handle/slug`. Before that, and whenever the API failed, the
   * organizer's own href, and failing that a permalink anchor nested INSIDE their mount div.
   *
   * That last branch is not decorative and it is the one Task 4 shipped with no test at all: a
   * button-mode mount div (`<div data-thrivia-event data-thrivia-mode="button"><a href="...">`, the
   * generated snippet's own shape) has no href of its own, so without it a visitor whose overlay
   * could not open would be handed nothing during the window before data arrives, and permanently
   * whenever the API is down. It is now reached by the popup-blocked and dead-frame paths and
   * asserted by both.
   */
  function fallbackUrl(record: MountRecord, eventId: string | null): string | null {
    // The clicked CTA's own event first. A list mount holds many cards and `record.card` is null
    // for all of them, so answering per-mount would hand a visitor whose overlay could not open
    // the permalink of the FIRST card in the list whichever one they pressed.
    // `items` is null on every mount that is not a list, so the length is read only after the
    // array is known to be there.
    if (eventId && record.items) {
      for (let i = 0; i < record.items.length; i++) {
        if (record.items[i].id === eventId) return record.items[i].url;
      }
    }
    if (record.card) return record.card.url;
    const own = record.el.getAttribute("href");
    if (own) return own;
    const anchor = record.el.querySelector<HTMLAnchorElement>("a[href]");
    // Their own code, not a destination: it must not reach `window.open` or an href we render.
    return anchor && !isScriptHref(anchor.href) ? anchor.href : null;
  }

  /**
   * Which event a click is for.
   *
   * A button mount is one element for one event, so the record answers it. Every RENDERED surface
   * is different: the listener sits on the mount and catches clicks from the whole subtree, so the
   * question is not "which event does this mount hold" but "which control did they press". A list
   * mount holds many cards and therefore many events; a card holds one event but also holds a
   * title link, a cover link and, in its ended state, a link to the creator's other trips - and
   * none of those three should ever open a checkout.
   *
   * So a rendered control opts IN by carrying the id, stamped by whoever drew it, and a click that
   * lands anywhere else resolves to null and is left alone to navigate. That is the whole guard:
   * no state test, no class test, and no list of exceptions to keep in step with the renderers.
   */
  function clickedEventId(record: MountRecord, event: MouseEvent): string | null {
    const target = event.target as Element | null;
    if (target && target.closest) {
      const owner = target.closest("[" + DT + "id]");
      if (owner) return owner.getAttribute(DT + "id");
    }
    return record.mode === "button" ? record.eventId : null;
  }

  /** Which flow the pressed control asked for: the brochure form, or (null) the booking flow. */
  function clickedIntent(event: MouseEvent): "brochure" | null {
    const target = event.target as Element | null;
    const owner = target && target.closest ? target.closest("[" + DT + "intent]") : null;
    return owner && owner.getAttribute(DT + "intent") === "brochure" ? "brochure" : null;
  }

  /**
   * A click on a bound mount: always a `button` mount, and now also a card, list or bar mount
   * whose organizer chose `data-thrivia-cta="overlay"`.
   *
   * Three outcomes, in order:
   *
   * 1. The element's own href is `javascript:`. That is the organizer's code running on their own
   *    click, so we stand down entirely: no preventDefault, no overlay, their handler untouched.
   * 2. No event id, per `clickedEventId`. Two cases reach it and both want the same answer. An
   *    autolinked `/@handle/slug` anchor has no id anywhere: the beacon IS the upgrade for those,
   *    the link then navigates exactly as it always did (the beacon uses `keepalive`, so it
   *    survives the navigation), and there is no public endpoint that resolves a slug to an id.
   *    A click inside a rendered surface that did not land on a CTA is the other, and leaving it
   *    alone is what keeps a card's title, cover and "See other events" links working as links.
   * 3. Otherwise the checkout opens, synchronously, inside this handler. `openCheckout` picks the
   *    overlay or a popup from `platform.sandboxed`, which was computed at boot for exactly this
   *    reason: a popup blocker kills anything opened after an await.
   *
   * `checkout_open` is conditioned on the return value, so a popup the browser refused is not
   * counted as an open checkout.
   */
  function onMountClick(record: MountRecord, event: MouseEvent): void {
    try {
      const eventId = clickedEventId(record, event);
      // A click inside a RENDERED surface that did not land on a CTA is not ours in any sense: not
      // to act on, and not to count either. The listener sits on the mount, so it hears the card's
      // title link, its cover, the notify-me trigger and anything the organizer put inside the
      // mount alongside us - and `embed_click` has to keep meaning "they pressed Book now" or the
      // creator's click-through rate becomes a measure of how much text their card has.
      //
      // A `button` mount is exempt because the whole element IS the CTA, including an autolinked
      // anchor that carries no id at all, where the beacon is the only thing the upgrade does.
      if (!eventId && record.mode !== "button") return;

      // An itinerary-PDF press is not a Book now press, so it counts toward neither beacon: both
      // feed the creator's booking funnel, and a brochure download is not a step in it.
      const intent = clickedIntent(event);
      if (!intent) tracker.track("embed_click", record.sid, record.mode, record.preview);

      const own = record.el.getAttribute("href");
      if (own && isScriptHref(own)) return;
      if (!eventId) return;

      // A held modifier means the visitor asked their browser for a new tab or window, and every
      // CTA under overlay mode is a real anchor with a real href. Swallowing that would be taking
      // an interaction away from them rather than adding one. The beacon above still counts it: it
      // was a click on our CTA either way. (Middle click arrives as `auxclick`, never here.)
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button) return;

      event.preventDefault();
      const opened = openCheckout({
        origin: config.origin,
        // Non-null here: boot returns early without a key, so nothing is ever bound without one.
        key: config.key || "",
        eventId: eventId,
        sid: record.sid,
        fallback: fallbackUrl(record, eventId),
        sandboxed: platform.sandboxed,
        intent: intent,
      });
      if (opened && !intent) tracker.track("checkout_open", record.sid, record.mode, record.preview);
    } catch (error) {
      // window.open throws outright in some sandboxed frames, and a throw inside a listener is an
      // uncaught error event on the organizer's page.
      log("click fail", error);
    }
  }

  /**
   * Watches for mounts that appear after boot.
   *
   * Not a nicety. Webflow and Wix inject their sections after their own scripts run, and any React
   * or Vue host renders its tree after ours has executed, so a loader that scans once finds nothing
   * on a large share of real websites.
   */
  function observeMutations(): void {
    if (typeof MutationObserver !== "function") return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let burstStart = 0;

    const fire = (): void => {
      timer = null;
      scan();
    };

    new MutationObserver(() => {
      try {
        const now = Date.now();
        if (timer === null) {
          burstStart = now;
        } else if (now - burstStart >= RESCAN_MAX_WAIT_MS) {
          // A trailing-only debounce STARVES. Any host that mutates faster than the debounce
          // window (a carousel, a chat widget, a framework re-rendering on an interval) pushes the
          // timer forward on every mutation and the scan never runs at all, which is precisely the
          // kind of host this observer exists for. The max wait bounds it: a busy page still gets
          // discovery, at worst one second late.
          clearTimeout(timer);
          fire();
          return;
        } else {
          clearTimeout(timer);
        }
        timer = setTimeout(fire, RESCAN_DEBOUNCE_MS);
      } catch (error) {
        log("rescan fail", error);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /**
   * Wrapped, and NOT redundantly.
   *
   * On the `readyState === "loading"` path below, `start` is scheduled as a DOMContentLoaded
   * listener, which runs long after boot's own try/catch has returned. Constructing and attaching the
   * MutationObserver is bare DOM work, so a host that has replaced `MutationObserver` (a polyfill, a
   * test harness, an analytics wrapper) throws here and the throw becomes an uncaught error on the
   * organizer's page attributed to a script they pasted from us. `scan` guards itself; this guards
   * everything around it.
   */
  function start(): void {
    try {
      scan();
      observeMutations();
    } catch (error) {
      log("start fail", error);
    }
  }

  // An async tag in <head> routinely executes before the body has been parsed, so scanning now
  // would find an empty document and the MutationObserver would then fire once per parsed chunk,
  // turning one batched call into several. Waiting for the parser to finish makes the common page
  // exactly one scan and one request; the observer covers everything that arrives afterwards.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
}

// Guest code on somebody else's homepage. Anything that escapes boot surfaces as an uncaught error
// in the visitor's console and in whatever error reporting the organizer runs, attributed to a
// script they pasted from us. There is no state to recover here, so the only correct response is to
// stop being present. Deliberately silent: `config` may not exist yet, so there is not even a debug
// flag to consult.
try {
  boot();
} catch {
  /* the widget does not load; the organizer's page is untouched */
}
